"use client";

import { useMemo } from "react";
import type { ModelSummary } from "@/hooks/useStationHubData";
import {
  BIKE_CLASSES,
  bikeLicenseText,
  getBikeClass,
  type BikeClassKeyOrUnknown,
} from "@/lib/mobility/bike-classes";
import { pickLabel, useHub, useHubDensity } from "./hub-context";
import {
  HUB_GUTTER,
  HubCell,
  hubListClass,
  isNewTabClick,
  SectionHeader,
} from "./hub-ui";
import { periodTotal } from "@/lib/mobility/pricing";
import { ItemRow } from "./ItemRow";
import { useWindowedCount } from "./useWindowedCount";

type ListItem =
  | { kind: "header"; key: string; title: string; meta: string }
  | { kind: "row"; key: string; model: ModelSummary };

/**
 * 차종 카탈로그 시트(V0) — 클래스별 섹션 아래 모델 행. spec §3-1.
 */
/** 차종 행에 보여줄 기간(일) — 지점의 차종 목록(StationBikesSheet)과 동일 */
const ROW_PERIODS = [1, 3, 7] as const;

export function CatalogSheet({ models }: { models: ModelSummary[] }) {
  const { t, locale, open, formatPrice, facetsOf, productHref, modelLink } = useHub();
  // 데스크톱 목록 열에서는 2열 카드(09-23 A안).
  const wide = useHubDensity() === "wide";
  const spanClass = wide ? "col-span-2" : undefined;


  // 섹션 헤더 + 모델 행을 한 배열로 펴서 24개씩 창으로 그린다.
  const items = useMemo<ListItem[]>(() => {
    const byClass = new Map<BikeClassKeyOrUnknown, ModelSummary[]>();
    for (const model of models) {
      const key = model.classKey || "unknown";
      const list = byClass.get(key);
      if (list) list.push(model);
      else byClass.set(key, [model]);
    }
    const sectionOf = (key: BikeClassKeyOrUnknown) => {
      const cls = getBikeClass(key);
      return {
        key,
        title: cls ? pickLabel(cls.label, locale) : "",
        license: bikeLicenseText(key, locale) ?? "",
        models: byClass.get(key) ?? [],
      };
    };
    const sections = [
      ...BIKE_CLASSES.filter((c) => byClass.has(c.key)).map((c) =>
        sectionOf(c.key)
      ),
      ...(byClass.has("unknown") ? [sectionOf("unknown")] : []),
    ];

    const flat: ListItem[] = [];
    for (const section of sections) {
      if (section.title) {
        flat.push({
          kind: "header",
          key: `h:${section.key}`,
          title: section.title,
          meta: section.license,
        });
      }
      for (const model of section.models) {
        flat.push({ kind: "row", key: model.key, model });
      }
    }
    return flat;
  }, [models, locale]);

  const { visible, hasMore, attachSentinel } = useWindowedCount(items.length);

  return (
    <div className="pb-8">
      {models.length === 0 ? (
        <p
          className={`${HUB_GUTTER} py-10 text-center text-[13px] text-gray-500`}
        >
          {t("hub.noResult")}
        </p>
      ) : (
        <div
          className={`mt-2 ${hubListClass(
            wide,
            // 테두리·구분선 대신 여백으로 구분(09-29, 지점 목록과 같은 규칙)
            "flex flex-col gap-4",
            "grid grid-cols-2 items-start gap-x-5 gap-y-7",
          )}`}
        >
          {items.slice(0, visible).map((item) => {
            if (item.kind === "header") {
              return (
                <SectionHeader
                  key={item.key}
                  title={item.title}
                  meta={item.meta}
                  thin
                  className={spanClass}
                />
              );
            }
            // 모델의 제원은 요금제 상품들이 같으므로 첫 상품 것을 쓴다.
            // 첫 상품 = 대표 상품(최저 1일가, aggregateModels 정렬 — 모델은 상품이 늘 1개 이상).
            const lead = item.model.products[0];
            const facets = facetsOf(lead.id);
            return (
              <HubCell key={item.key} wide={wide} bare>
              {/* 모델 허브(없으면 대표 상품 상세)의 <a href>(크롤러용) — 일반 클릭은 이동하지
                  않고 지금처럼 차종 시트를 연다. 새 탭 열기만 링크로(isNewTabClick). */}
              <a
                href={modelLink?.(item.model.key)?.path ?? productHref(lead.id)}
                onClick={(event) => {
                  if (isNewTabClick(event)) return;
                  event.preventDefault();
                  open({ kind: "model", modelKey: item.model.key });
                }}
                className="block w-full text-left transition hover:bg-gray-50"
              >
                <ItemRow
                  image={item.model.image}
                  name={item.model.name}
                  classKey={item.model.classKey}
                  bikeType={facets?.bikeType}
                  transmission={facets?.transmission}
                  rideStyles={facets?.rideStyles}
                  extraMeta={t("hub.stationsN", {
                    n: item.model.stationIds.size,
                  })}
                  periodPrices={ROW_PERIODS.map((days) => {
                    // 모델의 요금제 상품(인기/스탠다드) 중 최저 총액
                    const total = item.model.products.reduce((min, product) => {
                      const value = periodTotal(product, days) ?? 0;
                      return value > 0 && (min === 0 || value < min)
                        ? value
                        : min;
                    }, 0);
                    return {
                      label: t("hub.priceHead.d" + days),
                      value:
                        total > 0
                          ? formatPrice(
                              total,
                              item.model.currency ??
                                item.model.products[0]?.currency
                            )
                          : null,
                    };
                  })}
                />
              </a>
              </HubCell>
            );
          })}
          {hasMore ? (
            <div ref={attachSentinel} className={`h-8 ${spanClass ?? ""}`} />
          ) : null}
        </div>
      )}
    </div>
  );
}
